import { formatDateWithFormatter } from '@/utils/function'
import { paths } from '@/utils/path'
import { useTranslation } from 'react-i18next'
import { generatePath, Link } from 'react-router-dom'
import { getExpectedDateStatus } from './helpers'
import { NegotiationsSectionProps } from './types'

const NegotiationsSection = ({ negotiations }: NegotiationsSectionProps) => {
    const { t } = useTranslation()

    if (negotiations.length === 0) return null

    return (
        <div className="p-2 border-t border-gray-100 space-y-2">
            <div className="text-[10px] font-medium text-emerald-700 uppercase">
                {t('negotiations')} ({negotiations.length})
            </div>
            {negotiations.slice(0, 3).map((negotiation) => {
                const deadlineStatus = getExpectedDateStatus(
                    negotiation.contract_negotiation_deadline
                )

                return (
                    <Link
                        key={negotiation.contract_negotiation_id}
                        to={generatePath(paths.contractNegotiationDetail, {
                            id: negotiation.contract_negotiation_id.toString(),
                        })}
                        target="_blank"
                        className="block p-2 bg-emerald-50 rounded border border-emerald-100 hover:border-emerald-300 hover:bg-emerald-100/50 transition-colors"
                    >
                        <div className="flex items-center justify-between gap-2 mb-1">
                            <span className="text-xs font-medium text-emerald-700 truncate hover:underline">
                                {negotiation.contract_negotiation_title ||
                                    negotiation.contract_negotiation_code}
                            </span>
                            <span
                                className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                                    negotiation.contract_negotiation_status ===
                                    'completed'
                                        ? 'bg-emerald-200 text-emerald-700'
                                        : negotiation.contract_negotiation_status ===
                                            'cancelled'
                                          ? 'bg-gray-200 text-gray-600'
                                          : negotiation.contract_negotiation_status ===
                                              'rejected'
                                            ? 'bg-red-100 text-red-600'
                                            : negotiation.contract_negotiation_status ===
                                                'approved'
                                              ? 'bg-green-100 text-green-600'
                                              : 'bg-blue-100 text-blue-600'
                                }`}
                            >
                                {t(
                                    `negotiations.${negotiation.contract_negotiation_status}`
                                )}
                            </span>
                        </div>

                        {/* Type & Stage */}
                        <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                            {negotiation.contract_negotiation_type && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/60 text-emerald-600">
                                    {t(
                                        `negotiations.${negotiation.contract_negotiation_type}`
                                    )}
                                </span>
                            )}
                            {negotiation.contract_negotiation_stage && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/60 text-gray-500">
                                    {t(
                                        `negotiations.${negotiation.contract_negotiation_stage}`
                                    )}
                                </span>
                            )}
                        </div>

                        <div className="flex items-center gap-2 text-[10px] text-emerald-600">
                            {negotiation.contract_negotiation_value > 0 && (
                                <>
                                    <span className="font-medium">
                                        {new Intl.NumberFormat('vi-VN').format(
                                            negotiation.contract_negotiation_value
                                        )}{' '}
                                        đ
                                    </span>
                                    {negotiation.contract_negotiation_deadline &&
                                        negotiation.contract_negotiation_status !==
                                            'completed' && (
                                            <span className="text-gray-300">
                                                •
                                            </span>
                                        )}
                                </>
                            )}
                            {negotiation.contract_negotiation_deadline &&
                                negotiation.contract_negotiation_status !==
                                    'completed' && (
                                    <span
                                        className={
                                            deadlineStatus.isWarning
                                                ? deadlineStatus.isOverdue
                                                    ? 'text-red-600'
                                                    : 'text-amber-600'
                                                : ''
                                        }
                                    >
                                        {deadlineStatus.isOverdue
                                            ? t('sale-process.overdue-days', {
                                                  days: deadlineStatus.daysLeft,
                                              })
                                            : deadlineStatus.daysLeft === 0
                                              ? t('due-today')
                                              : deadlineStatus.isWarning
                                                ? t(
                                                      'sale-process.due-in-days',
                                                      {
                                                          days: deadlineStatus.daysLeft,
                                                      }
                                                  )
                                                : formatDateWithFormatter(
                                                      negotiation.contract_negotiation_deadline,
                                                      'DD/MM/YYYY'
                                                  )}
                                    </span>
                                )}
                        </div>

                        {/* Assignees */}
                        {negotiation.assignee_names &&
                            negotiation.assignee_names.length > 0 && (
                                <div className="mt-1 text-[10px] text-gray-500 truncate">
                                    {negotiation.assignee_names
                                        .slice(0, 2)
                                        .join(', ')}
                                    {negotiation.assignee_names.length > 2 &&
                                        ` +${negotiation.assignee_names.length - 2}`}
                                </div>
                            )}

                        {negotiation.contract_negotiation_progress > 0 && (
                            <div className="mt-1.5">
                                <div className="w-full h-1 bg-emerald-100 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-emerald-500 rounded-full transition-all"
                                        style={{
                                            width: `${negotiation.contract_negotiation_progress}%`,
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                    </Link>
                )
            })}
            {negotiations.length > 3 && (
                <div className="text-[10px] text-center text-emerald-600">
                    +{negotiations.length - 3} {t('more')}
                </div>
            )}
        </div>
    )
}

export default NegotiationsSection
